import * as Luminous from 'luminous-lightbox';
import * as LuminousGallery from 'luminous-lightbox-gallery';
import Lawyer from './plugin/lawyer/lawyer';
import Stats from './content/stats';
import Gallery from './content/gallery';
import News from './plugin/news/news';
import Blog from './plugin/blog/blog';
import Expertise from './plugin/expertise/expertise';
import Sector from './plugin/sector/sector';
import Publication from './plugin/publication/publication';
import Event from './plugin/event/event';
import {Carousel, Collapse} from 'bootstrap';
import GeneralUtility from './utility/general-utility';
import ConsentManager from './extension/consent-manager';

/**
 * Main entry point for the website.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default class Main {
  constructor() {
    this.initializePreloader();
    this.initializeMainNavigation();
    this.initializeMainCarousel();
    this.initializeLuminous();
    this.initializePlugins();
    this.initializeExtensions();
    this.initializeContentElements();

    // Listen to device orientation changes
    this.adjustDeviceOrientation(GeneralUtility.getDeviceOrientation());
    GeneralUtility.deviceOrientationChangeEvent.register(orientation => {
      this.adjustDeviceOrientation(orientation);
    });
  }


  /**
   * Adjusts the orientation.
   *
   * @param {string} orientation Device orientation
   * @returns {void}
   * @private
   */
  private adjustDeviceOrientation(orientation: string): void {
    if (orientation === 'landscape') {
      document.body.classList.add('landscape');
      document.body.classList.remove('portrait');
    } else {
      document.body.classList.remove('landscape');
      document.body.classList.add('portrait');
    }
  }


  /**
   * Initializes the "fake" website preloader animation.
   *
   * @returns {void}
   * @private
   */
  private initializePreloader(): void {
    // Preloader is show by default (body-class is set by TypoScript); hide after a short delay
    setTimeout(() => {
      this.hidePreloader();
    }, 500);

    // Hook into all links that shall show the preloader on click
    document.querySelectorAll('.show-preloader').forEach((link: HTMLElement) => {
      link.addEventListener('click', e => {
        // Don't show preloader, if the CTRL-key is pressed (opens the link in a new window)
        if (e.ctrlKey) {
          return;
        }

        this.showPreloader();
      });
    });
  }


  /**
   * Displays the "fake" website preloader animation.
   *
   * @returns {void}
   */
  showPreloader() {
    document.body.classList.add('preloader-visible');
  }


  /**
   * Hides the "fake" website preloader animation.
   *
   * @returns {void}
   */
  hidePreloader() {
    document.body.classList.add('preloader-hiding');

    setTimeout(() => {
      document.body.classList.remove('preloader-hiding', 'preloader-visible');
    }, 250);
  }


  /**
   * Initializes the main navigation.
   *
   * @returns {void}
   * @private
   */
  private initializeMainNavigation(): void {
    const mainNavigationContainer = document.body.querySelector('#mainNavigation');
    mainNavigationContainer.addEventListener('show.bs.collapse', () => {
      document.body.classList.add('main-navigation-open');
    });
    mainNavigationContainer.addEventListener('hide.bs.collapse', () => {
      document.body.classList.remove('main-navigation-open');
    });

    // Prevent redirect on click on accordions. They should only open the submenu.
    const accordionTogglers = mainNavigationContainer.querySelectorAll('[data-bs-toggle="collapse"]');
    accordionTogglers.forEach((accordionToggler: HTMLElement) => {
      const mainNavigationLink = accordionToggler.parentElement.parentElement.querySelector('.nav-link');
      const collapseContainer = document.querySelector(accordionToggler.dataset.bsTarget);

      if (mainNavigationLink && collapseContainer) {
        const collapse = new Collapse(collapseContainer, {
          'toggle': false
        });

        mainNavigationLink.addEventListener('click', e => {
          e.preventDefault();
          collapse.toggle();
        });
      }
    });
  }


  /**
   * Initializes the main start-page carousel.
   *
   * @returns {void}
   * @private
   */
  private initializeMainCarousel(): void {
    const mainCarouselContainer = document.querySelector('.page-hero .carousel');
    if (mainCarouselContainer) {
      const slides = mainCarouselContainer.querySelectorAll<HTMLElement>('.carousel-item');

      // Start replacing all placeholder-images with actual images
      slides.forEach(slide => {
        const image = slide.querySelector<HTMLImageElement>('.image');
        image.src = image.dataset.src;
      });

      // Start at a random slide
      const randomSlideIndex = GeneralUtility.getRandomInteger(0, slides.length - 1);
      const firstSlide = slides.item(randomSlideIndex);
      firstSlide.classList.add('active');

      // Initialize the carousel
      new Carousel(mainCarouselContainer, {
        'ride': 'carousel',
        'interval': 7500,
        'touch': false,
        'keyboard': false,
        'pause': false
      });
    }
  }


  /**
   * Initializes the luminous module.
   *
   * @returns {void}
   * @private
   */
  private initializeLuminous(): void {
    const links = document.body.querySelectorAll('a.lightbox');
    const groups: { [key: string]: [Element?] } = {};

    links.forEach(link => {
      const rel = link.getAttribute('rel');

      // Group links into same rel attributes
      if (!groups.hasOwnProperty(rel)) {
        groups[rel] = [];
      }

      groups[rel].push(link);
    });

    const lightboxOptions: any = {
      /**
       * Returns the caption for an element.
       *
       * @param {HTMLElement} trigger Element that triggered the lightbox.
       */
      'caption': ((trigger: HTMLElement) => {
        const figure = trigger.parentElement;
        if (!figure || !figure.nextSibling) {
          return '';
        }

        const after = figure.nextSibling as HTMLElement;
        if (after.innerText) {
          return after.innerText;
        }

        return '';
      })
    };

    const galleryOptions: any = {
      'arrowNavigation': true
    };

    // Initialize simple lightbox or gallery for the groups
    for (const rel of Object.keys(groups)) {
      const group = groups[rel];

      if (group.length === 1) {
        new Luminous(group[0], lightboxOptions);
      } else {
        new LuminousGallery(group, galleryOptions, lightboxOptions);
      }
    }
  }


  /**
   * Initializes all plugins.
   *
   * @returns {void}
   * @private
   */
  private initializePlugins(): void {
    new Blog(document.querySelectorAll('[data-plugin="blog"]'));
    new Event(document.querySelectorAll('[data-plugin="event"]'));
    new Expertise(document.querySelectorAll('[data-plugin="expertise"]'));
    new Sector(document.querySelectorAll('[data-plugin="sector"]'));
    new Lawyer(document.querySelectorAll('[data-plugin="lawyer"]'));
    new News(document.querySelectorAll('[data-plugin="news"]'));
    new Publication(document.querySelectorAll('[data-plugin="publication"]'));
  }


  /**
   * Initializes other TYPO3 extensions.
   *
   * @returns {void}
   * @private
   */
  private initializeExtensions(): void {
    new ConsentManager();
  }


  /**
   * Initializes all content elements.
   *
   * @returns {void}
   * @private
   */
  private initializeContentElements(): void {
    const statsContainers = document.querySelectorAll('[data-ctype="sitepackage_stats"]');
    statsContainers.forEach((container: HTMLElement) => {
      new Stats(container);
    });

    const galleryContainers = document.querySelectorAll('[data-ctype="sitepackage_gallery"]');
    galleryContainers.forEach((container: HTMLElement) => {
      new Gallery(container);
    });
  }
}
